"use client";
import {
  ButtonComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useConfirmRplAddressMutation,
  useFlushPanMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
} from "@/lib/redux";
import { Grid, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import FaceVerificationModal from "../kyc/face-verification/face-verification-modal";
import PersonalAddressComponent from "./address";
import PersonalDetailsFields from "./personal-detail-fields";
import { checkForRpl } from "@/lib/utils/rpl.utils";

function PersonalDetailsScreen({ product }) {
  const router = useRouter();
  const { setActions } = useFooter();
  const t = useTranslations("PersonalDetails");
  const [showErrorFaceMatchModal, setShowErrorFaceMatchModal] = useState(false);
  const [showPanEditModal, setShowPanEditModal] = useState(false);
  const [
    flushPan,
    { isFetching: flushingPan, data: flushPanData, error: flushPanError },
  ] = useFlushPanMutation();

  const {
    data: applicationData,
    refetch: refetchApplicationData,
    isFetching: isFetchingApplication,
    error: applicationFetchError,
  } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const {
    data: customerData,
    error: errorGetCustomer,
    isLoading: customerLoading,
    refetch: refetchCustomerData,
  } = useGetCustomerQuery("");

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );


  const [confirmRplAddress, { data: confirmRplAddressData, isLoading: isConfirmingRplAddress }] = useConfirmRplAddressMutation();

  const isRPL = checkForRpl(productApplication);


  useEffect(() => {
    if (productApplication?.stage === STAGE.PERSONAL_DETAILS) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.PERSONAL_DETAILS_STARTED,
        skip: isRPL,
        ...productApplication,
        ...customerData?.data,
      });
    }
    if (
      productApplication?.stage &&
      productApplication?.stage !== STAGE.PERSONAL_DETAILS &&
      productApplication?.stage !== STAGE.ADD_ADDRESS
    ) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication]);
  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });

  const handleConfirmRplAddress = useCallback(async () => {
    try {
      const resp = await confirmRplAddress({appId:productApplication?.appId }).unwrap();
    } catch (error) {
      if(error?.status >= 400){
        console.error("Error confirming RPL address:", error);
        router.replace("/");
      }
    }
  }, [ confirmRplAddress, productApplication?.appId ]);

  useEffect(() => {
    if (productApplication?.stage === STAGE.ADD_ADDRESS) {
      setActions({
        showConfirm: isRPL,
        showReject: true,
        rejectBtnLabel: "Back",
        onConfirm: isRPL && handleConfirmRplAddress,
        onReject:
          customerData?.data?.isPartner || productApplication?.ckycOtpVerified || isRPL
            ? () => router.push("/")
            : () => setShowPanEditModal(true),
      });
    }
  }, [setActions, productApplication, customerData,handleConfirmRplAddress, isRPL]);

  const isloading = isFetchingApplication || customerLoading || isConfirmingRplAddress;
  const isError = applicationFetchError || errorGetCustomer || flushPanError;
  useErrorHandling(isError);
  const showModalContent = {
    label: productApplication?.loanReapplyMessage
      ? productApplication?.loanReapplyMessage
      : `Sorry! We are unable to continue your loan application. Please re-apply on ${productApplication?.loanApplyDate}`,
    subLabel: "",
    buttonLabel: "OK",
    icon: IMAGES.rejectedLoan,
  };

  const backClick = () => {
    if (!productApplication?.ckycOtpVerified) {
      flushPan({ appId: productApplication?.appId });
    } else {
      router.push("/");
    }
  };
  return (
    <>
      <LoaderComponent open={isloading} />

      <Grid
        sx={{
          backgroundColor: { xs: "none", sm: "rgba(242, 247, 247, 0.6)" },
          borderRadius: 2,
          pb: 2,
          px: { xs: 0, sm: 1, md: 2 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={{ xs: "center", sm: "space-between" }}
        >
          <Typography
            variant="h5"
            display={{ xs: "none", sm: "block" }}
            color="#003128"
            fontWeight={600}
          >
            {t(isRPL ? "confirmAddressDetails":"personaldetails")}
          </Typography>

          <Typography
            fontWeight={500}
            color="#003128"
            textTransform="capitalize"
            align="center"
          >
            {"Hi,"} {customerData?.data?.firstName?.toLowerCase()}{" "}
            {customerData?.data?.lastName?.toLowerCase()}
          </Typography>
        </Stack>
        <PaperComponent
          elevation={0}
          paperStyle={{
            backgroundColor: WEB_COLORS.lightSkyBlue,
            px: { xs: 1, md: 2 },
            py: 1,
            borderRadius: 2,
          }}
        >
          <PersonalAddressComponent
            product={product}
            applicationData={applicationData}
            isFetchingApplication={isFetchingApplication}
            applicationFetchError={applicationFetchError}
          />
          {/* </PaperComponent> */}
        </PaperComponent>

        {productApplication?.stage === STAGE.PERSONAL_DETAILS && (
          <PersonalDetailsFields
            product={product}
            customerData={customerData}
            applicationData={applicationData}
            refetchCustomerData={refetchCustomerData}
            isFetchingApplication={isFetchingApplication}
            applicationFetchError={applicationFetchError}
            refetchApplicationData={refetchApplicationData}
            setShowPanEditModal={setShowPanEditModal}
          />
        )}
        {/* </PaperComponent> */}
      </Grid>
      <FaceVerificationModal
        data={showModalContent}
        open={showErrorFaceMatchModal}
        closeModal={() => setShowErrorFaceMatchModal(false)}
        handleModal={() => {
          refetchApplicationData();
          refetchCustomerData();
          setShowErrorFaceMatchModal(false);
          router.push("/"); //navigate to login screen
        }}
      />
      <ModalComponent
        open={showPanEditModal}
        handleModal={setShowPanEditModal}
        title={"Modify PAN"}
      >
        <Stack spacing={2} mx={2}>
          <Typography fontSize={16}>
            You've previously used the PAN below, Are you sure you want to
            update it?
          </Typography>
          <Typography
            align="center"
            fontSize={22}
            fontWeight={600}
            py={2}
            gutterBottom
          >
            {customerData?.data?.pan}
          </Typography>

          <Stack direction={"row"} spacing={4}>
            <ButtonComponent
              variant="outlined"
              type="reset"
              onClick={backClick}
            >
              Yes
            </ButtonComponent>
            <ButtonComponent onClick={() => setShowPanEditModal(false)}>
              No
            </ButtonComponent>
          </Stack>
        </Stack>
      </ModalComponent>
    </>
  );
}

export default PersonalDetailsScreen;
